import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UpgradeRequest {
  id: string
  tenant_id: string
  current_plan_id: string | null
  requested_plan_id: string
  proof_url: string | null
  status: 'pending' | 'proof_uploaded' | 'approved' | 'rejected'
  admin_note: string | null
  verified_at: string | null
  created_at: string
  plans: { id: string; name: string; slug: string; price_idr: number | null } | null
}

export interface AvailablePlan {
  id: string
  name: string
  slug: string
  description: string | null
  price_idr: number | null
  sort_order: number
  plan_features: { feature_key: string }[]
}

// ── Queries ───────────────────────────────────────────────────────────────────

/** Fetch the active (pending/proof_uploaded/rejected) upgrade request for this tenant. */
export function useActiveUpgradeRequest() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['upgrade-request', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_upgrade_requests')
        .select('*, plans:requested_plan_id(id, name, slug, price_idr)')
        .eq('tenant_id', tenantId!)
        .in('status', ['pending', 'proof_uploaded', 'rejected'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return data as unknown as UpgradeRequest | null
    },
  })
}

/** Fetch all active plans for the upgrade plan picker. */
export function useAvailablePlans() {
  return useQuery({
    queryKey: ['plans-public'],
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plans')
        .select('id, name, slug, description, price_idr, sort_order, plan_features(feature_key)')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
      if (error) throw error
      return (data ?? []) as AvailablePlan[]
    },
  })
}

// ── Mutations ─────────────────────────────────────────────────────────────────

/**
 * Submit a new upgrade request.
 * Uploads the proof file to Supabase Storage and inserts the request record.
 */
export function useSubmitUpgradeRequest() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({
      requestedPlanId,
      currentPlanId,
      proofFile,
    }: {
      requestedPlanId: string
      currentPlanId: string | null
      /** Optional — omit for downgrade requests where no proof of payment is needed. */
      proofFile?: File
    }) => {
      if (!tenantId) throw new Error('Tenant not found')

      // 1. Insert placeholder record to get the ID
      const { data: record, error: insertError } = await supabase
        .from('subscription_upgrade_requests')
        .insert({
          tenant_id: tenantId,
          current_plan_id: currentPlanId ?? null,
          requested_plan_id: requestedPlanId,
          status: 'pending',
        })
        .select('id')
        .single()
      if (insertError) throw insertError

      // Steps 2 & 3 only run when a proof file is provided (upgrades / trial conversions).
      // Downgrade requests stay in 'pending' with no proof_url — admin approves directly.
      if (proofFile) {
        const requestId = record.id

        // 2. Upload proof to subscription-proofs/{tenant_id}/{request_id}
        const ext = proofFile.name.split('.').pop() ?? 'jpg'
        const storagePath = `${tenantId}/${requestId}.${ext}`

        const { error: uploadError } = await supabase.storage
          .from('subscription-proofs')
          .upload(storagePath, proofFile, { upsert: false })
        if (uploadError) throw uploadError

        const { data: urlData } = supabase.storage
          .from('subscription-proofs')
          .getPublicUrl(storagePath)

        // 3. Update record with proof_url and status
        const { error: updateError } = await supabase
          .from('subscription_upgrade_requests')
          .update({ proof_url: urlData.publicUrl, status: 'proof_uploaded' })
          .eq('id', requestId)
        if (updateError) throw updateError
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['upgrade-request', tenantId] })
    },
  })
}

/**
 * Re-upload proof for a rejected request.
 * Replaces the existing proof file and resets status to proof_uploaded.
 */
export function useReuploadUpgradeProof() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({
      requestId,
      proofFile,
    }: {
      requestId: string
      proofFile: File
    }) => {
      if (!tenantId) throw new Error('Tenant not found')

      const ext = proofFile.name.split('.').pop() ?? 'jpg'
      const storagePath = `${tenantId}/${requestId}.${ext}`

      // Remove old file (ignore error if doesn't exist)
      await supabase.storage.from('subscription-proofs').remove([storagePath])

      // Upload new proof
      const { error: uploadError } = await supabase.storage
        .from('subscription-proofs')
        .upload(storagePath, proofFile, { upsert: true })
      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage
        .from('subscription-proofs')
        .getPublicUrl(storagePath)

      // Reset status to proof_uploaded
      const { error } = await supabase
        .from('subscription_upgrade_requests')
        .update({
          proof_url: urlData.publicUrl,
          status: 'proof_uploaded',
          admin_note: null,
        })
        .eq('id', requestId)
        .eq('tenant_id', tenantId)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['upgrade-request', tenantId] })
    },
  })
}
